import { CoreApi, Product, ProductEntity } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getMarketProducts } from 'ducks/products';
import { useSelector } from 'react-redux';
import {
  reportAddArticle,
  reportChangeArticle,
  reportDeleteArticle,
  reportInteractWithMenu,
  reportNavigateMenu
} from './reporters';
import { useRef } from 'react';
import { WidgetMeta } from 'contexts/widget';
import { BehaviorKey } from 'rangeConfig/@types';

export type MenuType = 'main' | 'selected' | 'sheet' | 'scene';

export type InteractionSource = BehaviorKey | 'customBehavior';

// Base props with widget data
export type BaseInteractionParams<T extends InteractionSource = InteractionSource> = WidgetMeta & {
  source?: T;
};

export type InteractionMeta = {
  targets?: Product[];
  listIndex?: number;
  color?: string | string[];
  direction?: string;
  actionTaken?: boolean;
  size?: {
    value: string | number;
    dimension?: string;
  };
  option?: string;
};

export type InteractionParams = BaseInteractionParams & {
  activeSorting?: string;
  meta?: InteractionMeta;
};

type Session = {
  tracker: InteractionTracker;
  params: InteractionParams;
  timeout: NodeJS.Timeout;
  startTime: number;
};

class InteractionTracker {
  private before: ProductEntity[] = [];
  private after: ProductEntity[] = [];
  private coreApi: CoreApi;
  private products: Product[];

  constructor(coreApi: CoreApi, products: Product[]) {
    this.coreApi = coreApi;
    this.products = products;
  }

  private result() {
    const placed = this.after.filter(a => !this.before.map(b => b.entityId).includes(a.entityId));
    const removed = this.before.filter(b => !this.after.map(a => a.entityId).includes(b.entityId));

    const placedArticles = placed
      .map(pe => {
        const product = this.products.find(p => p.coreProductId === pe.productId);
        return product && { articleNumber: product.globalItemNo, entityId: pe.entityId };
      })
      .filter(Boolean) as { articleNumber: string; entityId: number }[];

    const removedArticles = removed
      .map(re => {
        const product = this.products.find(p => p.coreProductId === re.productId);
        return product && { articleNumber: product.globalItemNo, entityId: re.entityId };
      })
      .filter(Boolean) as { articleNumber: string; entityId: number }[];

    return {
      placedArticles,
      removedArticles,
      placed: placedArticles.length,
      removed: removedArticles.length
    };
  }

  start() {
    this.before = this.coreApi.entities.getAllShoppingBagEntities();
    return this;
  }

  end() {
    this.after = this.coreApi.entities.getAllShoppingBagEntities();
    return this.result();
  }
}

export const useInteractionReporter = () => {
  const coreApi = useCore();
  const products = useSelector(getMarketProducts);
  const sessions = useRef<Record<string, Session>>({});
  const sessionTimeout = 60000; // ms

  const reportInteractionResult = (
    result: ReturnType<InteractionTracker['result']>,
    params: InteractionParams,
    durationMs: number
  ) => {
    const { placedArticles, removedArticles, placed, removed } = result;
    const { meta: interactionMeta, ...rest } = params;
    const { targets, ...meta } = interactionMeta ?? {};
    const interactionParams = { ...rest, durationMs, config: undefined };

    const ignoreTrigger = ['subMenuExited', 'selectedMenuEntered', 'selectedMenuExited'].includes(
      interactionParams.source as string
    );

    const navigateTrigger = [
      'productGroupEntered',
      'productGroupExited',
      'subMenuEntered',
      'listExpanded',
      'listCollapsed'
    ].includes(interactionParams.source as string);

    if (ignoreTrigger) return;

    if (navigateTrigger) {
      reportNavigateMenu({ ...interactionParams, meta });
    } else if (placed && removed) {
      reportChangeArticle({
        ...interactionParams,
        placedArticles,
        removedArticles,
        targets: targets?.map(p => p.globalItemNo) ?? [],
        meta
      });
    } else if (placed) {
      reportAddArticle({
        ...interactionParams,
        articles: placedArticles,
        targets: targets?.map(p => p.globalItemNo) ?? [],
        meta
      });
    } else if (removed) {
      reportDeleteArticle({
        ...interactionParams,
        articles: removedArticles,
        meta
      });
    } else {
      // Some interaction happened but had no effect
      reportInteractWithMenu({ ...interactionParams, meta });
    }
  };

  const reporter = {
    start: (sessionId: string, params: InteractionParams) => {
      const sessionState = sessions.current;

      if (sessionState[sessionId]) {
        return false;
      }

      const startTime = performance.now();
      const tracker = new InteractionTracker(coreApi, products).start();
      const timeout = setTimeout(() => {
        tracker.end();
        delete sessionState[sessionId];
      }, sessionTimeout);

      sessionState[sessionId] = { tracker, params, timeout, startTime };
      return true;
    },
    end: (sessionIdOrIds: string | string[]) => {
      const sessionIds = Array.isArray(sessionIdOrIds) ? sessionIdOrIds : [sessionIdOrIds];
      const sessionState = sessions.current;

      return sessionIds.map(sessionId => {
        if (!sessionState[sessionId]) {
          return false;
        }

        const { tracker, params, timeout, startTime } = sessionState[sessionId];
        const result = tracker.end();
        const endTime = performance.now();
        clearTimeout(timeout);
        reportInteractionResult(result, params, endTime - startTime);
        delete sessionState[sessionId];
        return true;
      });
    },
    wrap: async <T extends () => any>(params: InteractionParams, fn: T) => {
      const sessionId = 'wrappedFunctionSession';

      if (!reporter.start(sessionId, params)) {
        return fn();
      }

      const res = await Promise.resolve(fn());
      reporter.end(sessionId);

      return res;
    }
  };

  return reporter;
};
